// alertApi.js
import { API_BASE_URL } from './config.js';
import fetchWrapper from '../components/utility/fetchWrapper.js'

/** Normalize /api/v1/logging/alerts/labels response into keys + value map. */
export function normalizeLokiLabelsResponse(logsData) {
  if (!logsData || Array.isArray(logsData)) {
    return { keys: [], options: {} };
  }
  const data = logsData.labels ?? logsData;
  const keys = Object.keys(data || {}).filter((k) => Array.isArray(data[k]));
  const options = {};
  keys
    .sort((a, b) => String(a).localeCompare(String(b)))
    .forEach((k) => {
      options[k] = [...data[k]].sort((a, b) => String(a).localeCompare(String(b)));
    });
  return { keys: Object.keys(options), options };
}

export const alertApi = {
  getAlerts: async ({ timeoutMs = 20000, pipelineId } = {}) => {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), timeoutMs);
    try {
      const params = new URLSearchParams();
      if (pipelineId) params.append("pipeline_id", pipelineId);
      const qs = params.toString();
      const response = await fetchWrapper(
        `${API_BASE_URL}/api/v1/alerts/get_alerts/${qs ? `?${qs}` : ""}`,
        {
          signal: controller.signal,
        },
      );
      if (!response.ok) throw new Error("Failed to fetchWrapper alerts");
      return await response.json();
    } catch (err) {
      if (err?.name === "AbortError") {
        throw new Error("Alert rules request timed out");
      }
      throw err;
    } finally {
      clearTimeout(timer);
    }
  },
  getAlertCapabilities: async () => {
    const response = await fetchWrapper(`${API_BASE_URL}/api/v1/alerts/capabilities`);
    if (!response.ok) throw new Error("Failed to fetch alert capabilities");
    return await response.json();
  },
  getIntelligentAlertSuggestions: async ({
    clientId,
    awsAccountId,
    title,
    pipelineId,
    maxSuggestions = 6,
  } = {}) => {
    const params = new URLSearchParams();
    if (clientId) params.append("client_id", clientId);
    if (awsAccountId) params.append("aws_account_id", awsAccountId);
    if (title && String(title).trim()) params.append("title", String(title).trim());
    if (pipelineId) params.append("pipeline_id", String(pipelineId).trim());
    if (maxSuggestions) params.append("max_suggestions", String(maxSuggestions));

    const query = params.toString();
    const url = `${API_BASE_URL}/api/v1/alerts/suggestions${query ? `?${query}` : ""}`;
    const response = await fetchWrapper(url);
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to fetch intelligent alert suggestions: ${errorText}`);
    }
    return await response.json();
  },
  createAlert: async (alertData) => {
    const response = await fetchWrapper(`${API_BASE_URL}/api/v1/alerts/`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(alertData),
    });
    if (!response.ok) {
      let detail = "";
      try {
        const body = await response.json();
        detail = body?.detail || body?.message || JSON.stringify(body);
      } catch {
        detail = await response.text();
      }
      throw new Error(typeof detail === "string" ? detail : "Failed to create alert");
    }
    return await response.json();
  },
  createAmpAlert: async (alertData) => {
    const response = await fetchWrapper(`${API_BASE_URL}/api/v1/amp/alerts/`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(alertData),
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok || data.status === "Failure") {
      throw new Error(data.error_details?.message || "Failed to create AMP alert");
    }
    return data;
  },
  updateAmpAlert: async (alertId, alertData) => {
    const encodedId = encodeURIComponent(alertId);
    const response = await fetchWrapper(`${API_BASE_URL}/api/v1/amp/alerts/${encodedId}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(alertData),
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok || data.status === "Failure") {
      const detail = data.error_details?.message || "Failed to update AMP alert";
      throw new Error(detail);
    }
    return data;
  },
  deleteAmpAlert: async (alertId) => {
    const encodedId = encodeURIComponent(alertId);
    const response = await fetchWrapper(`${API_BASE_URL}/api/v1/amp/alerts/${encodedId}`, {
      method: "DELETE",
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok || data.status === "Failure") {
      throw new Error(data.error_details?.message || "Failed to delete AMP alert");
    }
    return data;
  },
  getAmpAlerts: async ({ limit = 100, syncFromAmp = false, timeoutMs = 20000 } = {}) => {
    const params = new URLSearchParams();
    if (limit) params.append("limit", String(limit));
    params.append("sync_from_amp", syncFromAmp ? "true" : "false");
    const query = params.toString();
    const url = `${API_BASE_URL}/api/v1/amp/alerts/${query ? `?${query}` : ""}`;
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), timeoutMs);
    try {
      const response = await fetchWrapper(url, {
        signal: controller.signal,
        skipCache: true,
      });
      if (!response.ok) throw new Error("Failed to fetch AMP alerts");
      return await response.json();
    } catch (err) {
      if (err?.name === "AbortError") {
        throw new Error("AMP alerts request timed out");
      }
      throw err;
    } finally {
      clearTimeout(timer);
    }
  },
  syncAmpAlertsFromWorkspace: async () => {
    const response = await fetchWrapper(`${API_BASE_URL}/api/v1/amp/alerts/sync/from-amp`, {
      method: "POST",
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(data.errors?.[0]?.error || "Failed to sync AMP alerts from workspace");
    }
    return data;
  },
  updateAlert: async (title, alertData) => {
    const encodedTitle = encodeURIComponent(title);
    const url = `${API_BASE_URL}/api/v1/alerts/update_alerts/${encodedTitle}`;
    const response = await fetchWrapper(url, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(alertData),
    });
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error("Failed to update alert: " + errorText);
    }
    return await response.json();
  },
  deleteAlert: async (alertId) => {
    const encodedId = encodeURIComponent(alertId);
    const url = `${API_BASE_URL}/api/v1/alerts/delete_alerts/${encodedId}`;
    const response = await fetchWrapper(url, { method: "DELETE" });
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error("Failed to delete alert: " + errorText);
    }
    return await response.json();
  },
  // getMetricsConfig: async () => {
  //   const response = await fetchWrapper(`${API_BASE_URL}/api/v1/alerts/metrics-config`);
  //   if (!response.ok) throw new Error("Failed to fetchWrapper metrics config");
  //   return await response.json();
  // },
   getMetricsConfig: async () => {
    const response = await fetchWrapper(`${API_BASE_URL}/api/v1/alerts/metrics/catalog`);      
    if (!response.ok) throw new Error("Failed to fetchWrapper metrics config");
 
    return await response.json();
  },
   getNotificationNames: async () => {
    const response = await fetchWrapper(`${API_BASE_URL}/api/v1/alerts/alertmanager/contact-points`);
    if (!response.ok) throw new Error("Failed to fetch contact points");
    return await response.json();
  },
  /** Get Alertmanager contact points (receivers) for the Contact Points tab. */
  getContactPoints: async () => {
    const response = await fetchWrapper(`${API_BASE_URL}/api/v1/alerts/alertmanager/contact-points`);
    if (!response.ok) throw new Error("Failed to fetch contact points");
    const data = await response.json();
    if (Array.isArray(data)) return data;
    if (Array.isArray(data?.receivers)) return data.receivers;
    return [];
  },
  createContactPoint: async (payload) => {
    const response = await fetchWrapper(`${API_BASE_URL}/api/v1/alerts/alertmanager/contact-points`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(errorText || "Failed to create contact point");
    }
    return await response.json();
  },
  updateContactPoint: async (name, payload) => {
    const encoded = encodeURIComponent(name);
    const response = await fetchWrapper(`${API_BASE_URL}/api/v1/alerts/alertmanager/contact-points/${encoded}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(errorText || "Failed to update contact point");
    }
    return await response.json();
  },
  deleteContactPoint: async (name) => {
    const encoded = encodeURIComponent(name);
    const base = (API_BASE_URL || "").replace(/\/+$/, "");
    const url = `${base}/api/v1/alerts/alertmanager/contact-points/${encoded}`;
    const response = await fetchWrapper(url, {
      method: "DELETE",
      redirect: "follow",
    });
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(errorText || "Failed to delete contact point");
    }
    return await response.json().catch(() => ({}));
  },
  getContactPointConfig: async (name) => {
    const encoded = encodeURIComponent(name);
    const response = await fetchWrapper(`${API_BASE_URL}/api/v1/alerts/alertmanager/contact-points/${encoded}/config`);
    if (!response.ok) throw new Error("Failed to fetch contact point config");
    return await response.json();
  },
  getAllDashBoardsUid: async () => {
    const response = await fetchWrapper(`${API_BASE_URL}/api/v1/alerts/dashboards`);      
    if (!response.ok) throw new Error("Failed to fetchWrapper metrics config");
 
    return await response.json();
  },
  getPannelByUid: async (uid) => {
    const response = await fetchWrapper(`${API_BASE_URL}/api/v1/alerts/dashboards/${uid}/panels`);      
    if (!response.ok) throw new Error("Failed to fetchWrapper metrics config");
 
    return await response.json();
  },
  getAlertmanagerGroups: async (state = "") => {
    const url = state
      ? `${API_BASE_URL}/api/v1/alerts/alertmanager/groups?state=${state}`
      : `${API_BASE_URL}/api/v1/alerts/alertmanager/groups`;
    const response = await fetchWrapper(url);
    if (!response.ok) throw new Error("Failed to fetchWrapper alert groups");
    return await response.json();
  },
  getSilences: async () => {
    const response = await fetchWrapper(`${API_BASE_URL}/api/v1/alerts/alertmanager/silences`);
    if (!response.ok) throw new Error("Failed to fetchWrapper silences");
    return await response.json();
  },
  createSilence: async (silenceData) => {
    const response = await fetchWrapper(`${API_BASE_URL}/api/v1/alerts/alertmanager/silences`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(silenceData),
    });
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error("Failed to create silence: " + errorText);
    }
    return await response.json();
  },
  updateSilence: async (silenceId, silenceData) => {
    const encodedId = encodeURIComponent(silenceId);
    // This endpoint triggers your backend composite update method.
    const url = `${API_BASE_URL}/api/v1/alerts/alertmanager/silences/${encodedId}`;
    const response = await fetchWrapper(url, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(silenceData),
    });
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error("Failed to update silence: " + errorText);
    }
    return await response.json();
  },
  deleteSilence: async (silenceId) => {
    const encodedId = encodeURIComponent(silenceId);
    const url = `${API_BASE_URL}/api/v1/alerts/alertmanager/silence/${encodedId}`;
    const response = await fetchWrapper(url, { method: "DELETE" });
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error("Failed to unsilence (delete) silence: " + errorText);
    }
    return await response.json();
  },
  getAmpSilences: async () => {
    const response = await fetchWrapper(`${API_BASE_URL}/api/v1/amp/alerts/alertmanager/silences`);
    if (!response.ok) throw new Error("Failed to fetch AMP silences");
    const data = await response.json();
    return data?.silences ?? data;
  },
  createAmpSilence: async (silenceData) => {
    const response = await fetchWrapper(`${API_BASE_URL}/api/v1/amp/alerts/alertmanager/silences`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(silenceData),
    });
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error("Failed to create AMP silence: " + errorText);
    }
    return await response.json();
  },
  updateAmpSilence: async (silenceId, silenceData) => {
    const encodedId = encodeURIComponent(silenceId);
    const url = `${API_BASE_URL}/api/v1/amp/alerts/alertmanager/silences/${encodedId}`;
    const response = await fetchWrapper(url, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(silenceData),
    });
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error("Failed to update AMP silence: " + errorText);
    }
    return await response.json();
  },
  deleteAmpSilence: async (silenceId) => {
    const encodedId = encodeURIComponent(silenceId);
    const url = `${API_BASE_URL}/api/v1/amp/alerts/alertmanager/silence/${encodedId}`;
    const response = await fetchWrapper(url, { method: "DELETE" });
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error("Failed to unsilence AMP silence: " + errorText);
    }
    return await response.json();
  },
  syncAmpAlertmanager: async () => {
    const response = await fetchWrapper(`${API_BASE_URL}/api/v1/amp/alerts/alertmanager/sync`, {
      method: "POST",
    });
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error("Failed to sync AMP Alertmanager: " + errorText);
    }
    return await response.json();
  },
  getAlertPageLogs: async ({ timeoutMs = 60000, keysOnly = false, clientId, awsAccountId } = {}) => {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), timeoutMs);
    const params = new URLSearchParams();
    if (keysOnly) params.set("keys_only", "true");
    const query = params.toString() ? `?${params.toString()}` : "";

    const scopedPath =
      clientId && awsAccountId
        ? `/api/v1/logging/alerts/labels/${encodeURIComponent(clientId)}/awsid-${encodeURIComponent(awsAccountId)}`
        : "/api/v1/logging/alerts/labels";

    try {
      const response = await fetchWrapper(
        `${API_BASE_URL}${scopedPath}${query}`,
        { signal: controller.signal, skipCache: false },
      );
      if (!response.ok) {
          if (response.status === 404) {
              console.log('no logs');
              return [];
          }
          throw new Error(`Failed to fetchWrapper logs (Status: ${response.status})`);
      }
      const data = await response.json();
      return data

    } catch (error) {
      if (error?.name === "AbortError") {
        throw new Error("Loki labels request timed out");
      }
      console.error("Error in getAlertPageLogs:", error);
      throw error;
    } finally {
      clearTimeout(timer);
    }
  },
  
  getFiringAlerts: async ({ timeoutMs = 20000, retries = 1, pipelineId, awsAccountId } = {}) => {
    let lastError = null;
    for (let attempt = 0; attempt <= retries; attempt += 1) {
      const attemptTimeout = timeoutMs + attempt * 10000;
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), attemptTimeout);
      try {
        const params = new URLSearchParams();
        if (pipelineId) params.append("pipeline_id", pipelineId);
        if (awsAccountId) {
          params.append(
            "aws_account_id",
            String(awsAccountId).replace(/^aws_/, ""),
          );
        }
        const qs = params.toString();
        const response = await fetchWrapper(
          `${API_BASE_URL}/api/v1/alerts/firing-alerts${qs ? `?${qs}` : ""}`,
          {
            signal: controller.signal,
            skipCache: attempt === 0,
          },
        );
        if (!response.ok) throw new Error("Failed to fetch firing alerts");
        return await response.json();
      } catch (err) {
        if (err?.name === "AbortError") {
          lastError = new Error("Firing alerts request timed out");
        } else {
          lastError = err;
        }
        const isTimeout = lastError?.message?.includes("timed out");
        if (!isTimeout || attempt >= retries) {
          throw lastError;
        }
      } finally {
        clearTimeout(timer);
      }
    }
    throw lastError || new Error("Failed to fetch firing alerts");
  },
  getDataSourceConfig: async () => {
    const response = await fetchWrapper(
      `${API_BASE_URL}/api/v1/alerts/datasources`
    );
    if (!response.ok) throw new Error("Failed to fetch datasources");
    return await response.json();
  },
};
